// Local Authentication Service - No backend required
class LocalAuthService {
  constructor() {
    this.users = this.loadUsers();
    this.currentUser = this.loadCurrentUser();
  }

  // Load users from localStorage
  loadUsers() {
    const users = localStorage.getItem('silva_users');
    return users ? JSON.parse(users) : [
      {
        id: '1',
        email: 'admin@silva.com',
        password: 'admin123', // In production, use proper hashing
        name: 'Admin Silva',
        role: 'admin',
        verified: true,
        createdAt: new Date().toISOString()
      },
      {
        id: '2',
        email: 'user@silva.com',
        password: 'user123',
        name: 'Usuario Demo',
        role: 'user',
        verified: true,
        createdAt: new Date().toISOString()
      }
    ];
  }

  // Save users to localStorage
  saveUsers() {
    localStorage.setItem('silva_users', JSON.stringify(this.users));
  }

  // Load current user from localStorage
  loadCurrentUser() {
    const user = localStorage.getItem('silva_current_user');
    return user ? JSON.parse(user) : null;
  }

  // Save current user to localStorage
  saveCurrentUser(user) {
    if (user) {
      localStorage.setItem('silva_current_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('silva_current_user');
    }
    this.currentUser = user;
  }

  // Generate JWT-like token
  generateToken(user) {
    const payload = {
      userId: user.id,
      email: user.email,
      role: user.role,
      exp: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
    };
    return btoa(JSON.stringify(payload)); // Simple encoding
  }

  // Verify token
  verifyToken(token) {
    try {
      const payload = JSON.parse(atob(token));
      if (payload.exp < Date.now()) {
        return null;
      }
      return payload;
    } catch (error) {
      return null;
    }
  }

  // Login user
  async login(email, password) {
    const user = this.users.find(u => u.email === email && u.password === password);
    
    if (!user) {
      throw new Error('Credenciales inválidas');
    }

    if (!user.verified) {
      throw new Error('Por favor verifica tu email antes de iniciar sesión');
    }

    const token = this.generateToken(user);
    const userWithoutPassword = { ...user };
    delete userWithoutPassword.password;

    this.saveCurrentUser(userWithoutPassword);
    localStorage.setItem('silva_token', token);

    return {
      success: true,
      token,
      user: userWithoutPassword,
      message: 'Inicio de sesión exitoso'
    };
  }

  // Register user
  async register(userData) {
    const existingUser = this.users.find(u => u.email === userData.email);
    
    if (existingUser) {
      throw new Error('El email ya está registrado');
    }

    const newUser = {
      id: Date.now().toString(),
      ...userData,
      role: 'user',
      verified: false,
      createdAt: new Date().toISOString()
    };

    this.users.push(newUser);
    this.saveUsers();

    // Simulate email verification
    setTimeout(() => {
      newUser.verified = true;
      this.saveUsers();
    }, 2000);

    return {
      success: true,
      message: 'Usuario registrado exitosamente. Verifica tu email.',
      user: { ...newUser, password: undefined }
    };
  }

  // Forgot password
  async forgotPassword(email) {
    const user = this.users.find(u => u.email === email);
    
    if (!user) {
      throw new Error('Email no encontrado');
    }

    // Simulate password reset email
    return {
      success: true,
      message: 'Se ha enviado un enlace de restablecimiento a tu email'
    };
  }

  // Reset password
  async resetPassword(token, password) {
    // In a real app, you'd verify the token
    const user = this.users.find(u => u.email === 'user@silva.com'); // Demo
    
    if (!user) {
      throw new Error('Token inválido o expirado');
    }

    user.password = password;
    this.saveUsers();

    return {
      success: true,
      message: 'Contraseña actualizada exitosamente'
    };
  }

  // Verify email
  async verifyEmail(token) {
    // Simulate email verification
    return {
      success: true,
      message: 'Email verificado exitosamente'
    };
  }

  // Logout user
  async logout() {
    this.saveCurrentUser(null);
    localStorage.removeItem('silva_token');
    
    return {
      success: true,
      message: 'Sesión cerrada exitosamente'
    };
  }

  // Get current user
  async getCurrentUser() {
    const token = localStorage.getItem('silva_token');
    if (!token) {
      return null;
    }

    const payload = this.verifyToken(token);
    if (!payload) {
      this.logout();
      return null;
    }

    const user = this.users.find(u => u.id === payload.userId);
    if (!user) {
      this.logout();
      return null;
    }

    const userWithoutPassword = { ...user };
    delete userWithoutPassword.password;
    
    return userWithoutPassword;
  }

  // Update user profile
  async updateProfile(userData) {
    const currentUser = await this.getCurrentUser();
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    const userIndex = this.users.findIndex(u => u.id === currentUser.id);
    if (userIndex === -1) {
      throw new Error('Usuario no encontrado');
    }

    // Update user data
    this.users[userIndex] = { ...this.users[userIndex], ...userData };
    this.saveUsers();

    const updatedUser = { ...this.users[userIndex] };
    delete updatedUser.password;
    
    this.saveCurrentUser(updatedUser);

    return {
      success: true,
      user: updatedUser,
      message: 'Perfil actualizado exitosamente'
    };
  }

  // Change password
  async changePassword(currentPassword, newPassword) {
    const currentUser = await this.getCurrentUser();
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    const user = this.users.find(u => u.id === currentUser.id);
    if (!user || user.password !== currentPassword) {
      throw new Error('Contraseña actual incorrecta');
    }

    user.password = newPassword;
    this.saveUsers();

    return {
      success: true,
      message: 'Contraseña cambiada exitosamente'
    };
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('silva_token');
    if (!token) return false;

    const payload = this.verifyToken(token);
    return payload !== null;
  }

  // Get auth token
  getToken() {
    return localStorage.getItem('silva_token');
  }

  // Set auth token
  setToken(token) {
    localStorage.setItem('silva_token', token);
  }

  // Remove auth token
  removeToken() {
    localStorage.removeItem('silva_token');
  }

  // Get all users (admin only)
  async getAllUsers() {
    const currentUser = await this.getCurrentUser();
    if (!currentUser || currentUser.role !== 'admin') {
      throw new Error('Acceso denegado');
    }

    return this.users.map(user => {
      const userWithoutPassword = { ...user };
      delete userWithoutPassword.password;
      return userWithoutPassword;
    });
  }

  // Delete user (admin only)
  async deleteUser(userId) {
    const currentUser = await this.getCurrentUser();
    if (!currentUser || currentUser.role !== 'admin') {
      throw new Error('Acceso denegado');
    }

    const userIndex = this.users.findIndex(u => u.id === userId);
    if (userIndex === -1) {
      throw new Error('Usuario no encontrado');
    }

    this.users.splice(userIndex, 1);
    this.saveUsers();

    return {
      success: true,
      message: 'Usuario eliminado exitosamente'
    };
  }
}

// Create singleton instance
const localAuthService = new LocalAuthService();

export default localAuthService; 